/**
 * La scheda di una fattura sul telefono (7/10/2026): risponde a "è pagata? quanto,
 * e da chi? cosa faccio adesso?".
 *
 * Prima, a 390px: il numero gigante, poi il ciclo di vita a quattro pallini
 * (Bozza, Emessa, Inviata, Pagata) che andava a capo, una nota "si fa dal
 * computer", le righe, e i dati del cliente e delle date in coda come modulo
 * (RAGIONE SOCIALE / P.IVA / SDI, EMESSA IL / DA PAGARE ENTRO…).
 * Qui:
 *   1. in grande, quanto vale; sopra, che documento è; sotto, se è pagata o da
 *      quanto è in ritardo (in rosso solo allora);
 *   2. il cliente (si apre la sua scheda); le date del pagamento; le righe
 *      della fattura; la nota interna, che si tocca e si cambia; da dove viene
 *      (preventivo, progetto);
 *   3. in fondo il gesto che serve adesso, lo stesso del computer: emettere,
 *      mandare al cliente, segnare incassata;
 *   4. nel menu ⋯ in alto il resto: il PDF su FattureInCloud, l'invio per
 *      email, l'aggiornamento da FattureInCloud, segna incassata oggi.
 * Stampa e invio all'Agenzia delle Entrate restano al computer (sono
 * irreversibili o fanno un documento): lo dice una riga in fondo.
 * Stesse chiamate, stessa cache e stessi messaggi della pagina del computer.
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { celebrate } from '../../stores/moment.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { ActionsMenu, type ActionItem } from '../../components/ui/ActionsMenu';
import { PageActions } from '../../components/layout/PageActions';
import { MCampo, MEmpty, MGroup, MHero, MPage, MRow, iniziali, leggibile } from '../../components/mobile/MobileKit';
import { quotesQueryOptions } from '../../lib/scadenziario';
import { fmtEur } from '../../lib/utils';
import { METHOD_LABEL, daysFromToday, type PayMethod } from '../admin/adminApi';
import { DueRighe, Foglio, euroEsatto, euroRiga, giornoBreve } from '../admin/shared/AdminMobile';
import { durata } from '../admin/fatture/fattureApi';

interface FatturaPiena {
  id: string;
  number: string | null;
  type: string;
  status: string;
  issueDate: string | null;
  dueDate: string | null;
  paidDate: string | null;
  subtotal: string;
  vat: string;
  total: string;
  paymentMethod: string | null;
  sdiId: string | null;
  notes: string | null;
  ficDocumentId: number | null;
  ficUrl: string | null;
  client: { id: string; legalName: string; shortName: string; vat: string | null; sdiCode: string | null; pec: string | null };
  project: { id: string; code: string; name: string } | null;
  lines: Array<{ id: string; description: string; quantity: string; unitPrice: string; vatRate: string }>;
}

const TIPO: Record<string, string> = { FATTURA: 'Fattura', NOTA_CREDITO: 'Nota di credito', PROFORMA: 'Pre-fattura', AVVISO_PAGAMENTO: 'Avviso di pagamento' };

/** La riga onesta su quello che dal telefono non si fa (mobile.md §8): irreversibile o un documento da archiviare. */
const DAL_COMPUTER = "Stampa e invio all'Agenzia delle Entrate si fanno dal computer.";

interface Invio { to: string; cc: string; subject: string; body: string }
type CampoInvio = keyof Invio;

export function FatturaMobile({ id }: { id: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: inv, isLoading, isError } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.get<FatturaPiena>(`/invoices/${id}`),
    retry: false,
  });
  const { data: ficStatus } = useQuery({
    queryKey: ['fic-status'],
    queryFn: () => api.get<{ connected: boolean; companyName: string | null }>('/settings/integrations/fic/status'),
    refetchOnWindowFocus: false,
  });
  // Il preventivo da cui è nata: la stessa cache della pagina del computer.
  const { data: quoteData } = useQuery({ ...quotesQueryOptions });
  const [nota, setNota] = useState<string | null>(null);
  const [confermaIncasso, setConfermaIncasso] = useState(false);
  const [invio, setInvio] = useState<Invio | null>(null);
  const [campo, setCampo] = useState<CampoInvio | null>(null);
  const [bozza, setBozza] = useState('');

  const rinfresca = (anche: string[][] = []) => {
    qc.invalidateQueries({ queryKey: ['invoice', id] });
    for (const k of anche) qc.invalidateQueries({ queryKey: k });
  };

  // ── Le stesse mutazioni della pagina del computer (stessi endpoint, stessi messaggi).
  const issue = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/issue`),
    onSuccess: () => { rinfresca([['invoices'], ['dashboard']]); toast.ok('Fattura emessa con la numerazione interna', 'Stato aggiornato e numero assegnato.'); },
    onError: (e: any) => toast.alert('Fattura non emessa', e?.message ?? 'Riprova'),
  });
  const markPaid = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/mark-paid`),
    onSuccess: () => {
      rinfresca([['invoices'], ['admin'], ['dashboard']]);
      celebrate({ title: 'Fattura incassata', amount: Number(inv?.total ?? 0), subtitle: inv?.number ? `Fattura ${inv.number}` : undefined, tone: 'ok' });
    },
    onError: (e: any) => toast.alert('Fattura non segnata incassata', e?.message ?? 'Riprova'),
  });
  const updateNotes = useMutation({
    mutationFn: (notes: string) => api.patch(`/invoices/${id}`, { notes }),
    onSuccess: () => { rinfresca(); setNota(null); toast.ok('Nota interna aggiornata'); },
    onError: (e: any) => toast.alert('Nota interna non salvata', e?.message ?? 'Riprova'),
  });
  const pushToFic = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/push-to-fic`),
    onSuccess: () => { rinfresca([['invoices'], ['dashboard']]); toast.ok('Fattura emessa su FattureInCloud', 'Il numero ufficiale lo ha dato FattureInCloud.'); },
    onError: (e: any) => toast.alert('Fattura non emessa su FattureInCloud', e?.message ?? 'Riprova'),
  });
  const syncFromFic = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/sync-from-fic`),
    onSuccess: () => { rinfresca(); toast.ok('Fattura aggiornata da FattureInCloud'); },
    onError: (e: any) => toast.alert('Fattura non aggiornata da FattureInCloud', e?.message ?? 'Riprova'),
  });
  const sendEmailFic = useMutation({
    mutationFn: (p: { to?: string; cc?: string; subject?: string; body?: string }) => api.post(`/invoices/${id}/send-email-fic`, p),
    onSuccess: () => { rinfresca(); setInvio(null); toast.ok('Fattura mandata al cliente per email', 'La mail è partita da FattureInCloud.'); },
    onError: (e: any) => toast.alert('Fattura non mandata al cliente', e?.message ?? 'Riprova'),
  });

  if (isError) return <MPage><MEmpty>Fattura non trovata: forse è stata cancellata, o il link è sbagliato.</MEmpty></MPage>;
  if (isLoading || !inv) return <MPage><MEmpty>Carico la fattura…</MEmpty></MPage>;

  const ficConnected = !!ficStatus?.connected;
  const onFic = !!inv.ficDocumentId;
  const bozzaFattura = inv.status === 'BOZZA';
  const pagata = inv.status === 'PAGATA';
  const annullata = inv.status === 'ANNULLATA';
  const aperta = ['EMESSA', 'INVIATA', 'SCADUTA'].includes(inv.status);
  const g = daysFromToday(inv.dueDate);
  const inRitardo = aperta && g != null && g < 0;
  const tipo = TIPO[inv.type] ?? inv.type;
  const quoteRef = quoteData?.items.find((q) => q.convertedToInvoice?.id === inv.id) ?? null;
  const apriInvio = () => setInvio({ to: '', cc: '', subject: `Fattura ${inv.number ?? 'documento'}`, body: '' });

  // Lo stato, detto come una frase: rosso solo il ritardo.
  const stato: ReactNode = pagata ? `pagata${inv.paidDate ? ` il ${giornoBreve(inv.paidDate)}` : ''}`
    : annullata ? 'annullata: non si può più modificare'
    : bozzaFattura ? 'bozza, non ancora emessa'
    : inRitardo ? <span className="is-alert">in ritardo da {durata(-g!)}</span>
    : inv.dueDate ? `da incassare entro il ${giornoBreve(inv.dueDate)}` : 'da incassare';

  // Il gesto che serve adesso: lo stesso ordine del computer (computeNextAction).
  let primary: { label: string; icon: IconName; onClick: () => void; pending?: boolean; key: string } | null = null;
  if (bozzaFattura) {
    primary = ficConnected
      ? { key: 'push-fic', label: 'Emetti su FattureInCloud', icon: 'send', onClick: () => pushToFic.mutate(), pending: pushToFic.isPending }
      : { key: 'issue', label: 'Emetti la fattura qui', icon: 'send', onClick: () => issue.mutate(), pending: issue.isPending };
  } else if (inv.status === 'EMESSA' && onFic) {
    primary = { key: 'send-email', label: 'Manda la fattura al cliente', icon: 'mail', onClick: apriInvio };
  } else if (inv.status === 'EMESSA' || inv.status === 'INVIATA') {
    primary = { key: 'mark-paid', label: 'Segna incassata', icon: 'check', onClick: () => setConfermaIncasso(true), pending: markPaid.isPending };
  } else if (inv.status === 'SCADUTA') {
    primary = onFic
      ? { key: 'send-email', label: 'Rimanda la fattura al cliente', icon: 'mail', onClick: apriInvio }
      : { key: 'mark-paid', label: 'Segna incassata', icon: 'check', onClick: () => setConfermaIncasso(true), pending: markPaid.isPending };
  }

  const menu: ActionItem[] = [
    { key: 'open-fic-pdf', label: 'Apri il PDF su FattureInCloud', icon: 'link', onClick: () => window.open(inv.ficUrl!, '_blank'), hidden: !inv.ficUrl },
    { key: 'send-email', label: 'Manda la fattura al cliente per email', icon: 'mail', onClick: apriInvio, hidden: !onFic },
    { key: 'mark-paid', label: 'Segna incassata oggi, con bonifico', icon: 'check', onClick: () => setConfermaIncasso(true), hidden: !aperta },
    { key: 'issue', label: 'Emetti la fattura qui', icon: 'send', onClick: () => issue.mutate(), hidden: !(bozzaFattura && !ficConnected) },
    { key: 'sync-fic', label: 'Aggiorna la fattura da FattureInCloud', icon: 'refresh', onClick: () => syncFromFic.mutate(), hidden: !onFic },
  ];

  const sdi = inv.client.sdiCode ?? inv.client.pec;
  const valoreInvio = (k: CampoInvio): string => {
    if (!invio) return '';
    if (k === 'to') return invio.to || 'il contatto principale';
    if (k === 'cc') return invio.cc || 'nessuno';
    if (k === 'subject') return invio.subject;
    return invio.body ? 'scritto da te' : 'quello di FattureInCloud';
  };

  return (
    <>
      <PageActions className="flex gap-2">
        <ActionsMenu variant="ghost" label="Azioni sulla fattura" items={menu} excludeKey={primary?.key}
          disabled={syncFromFic.isPending || markPaid.isPending || sendEmailFic.isPending || issue.isPending} />
      </PageActions>

      <MPage>
        <h1 className="sr-only">{tipo} {inv.number ?? 'senza numero'}</h1>
        <MHero
          label={`${tipo} ${inv.number ?? 'senza numero'}`}
          unit="€"
          value={fmtEur(inv.total)}
          sub={stato}
        />

        <MGroup title="Il cliente">
          <MRow text={iniziali(inv.client.legalName)} round title={leggibile(inv.client.legalName)}
            sub={inv.client.vat ? `P.IVA ${inv.client.vat}` : undefined} onClick={() => navigate(`/clients/${inv.client.id}`)} />
          {!sdi && !bozzaFattura && (
            <MRow icon="alert" tone="alert" title="Mancano codice SDI e PEC" sub="servono per mandare la fattura elettronica" />
          )}
        </MGroup>

        <MGroup title="Pagamento">
          <MRow icon="calendar" title="Emessa il" trail={giornoBreve(inv.issueDate) || '—'} trailTone="mute" />
          {!pagata && <MRow icon="calendarClock" tone={inRitardo ? 'alert' : undefined} title="Da pagare entro" trail={giornoBreve(inv.dueDate) || '—'} trailTone="mute" />}
          {pagata && <MRow icon="check" title="Pagata il" trail={giornoBreve(inv.paidDate) || '—'} trailTone="mute" />}
          {inv.paymentMethod && <MRow icon="banknote" title="Pagata con" trail={METHOD_LABEL[inv.paymentMethod as PayMethod] ?? inv.paymentMethod} trailTone="mute" />}
        </MGroup>

        <MGroup title="Cosa c'è in fattura" count={inv.lines.length || undefined}
          note={`Imponibile ${euroEsatto(Number(inv.subtotal))} più IVA ${euroEsatto(Number(inv.vat))}.`}>
          {inv.lines.length === 0 ? <MEmpty>Le righe sono su FattureInCloud: qui c'è solo il totale.</MEmpty>
            : inv.lines.map((l) => {
              const qty = Number(l.quantity) || 0;
              const prezzo = Number(l.unitPrice) || 0;
              const iva = Number(l.vatRate) || 0;
              return (
                <MRow key={l.id} title={<DueRighe>{l.description}</DueRighe>}
                  sub={`${qty} × ${euroEsatto(prezzo)} · IVA ${iva}%`}
                  trail={euroRiga(qty * prezzo * (1 + iva / 100))} />
              );
            })}
        </MGroup>

        <MGroup title="Nota interna" note={quoteRef || inv.project || onFic ? undefined : DAL_COMPUTER}>
          <MRow icon={inv.notes ? 'file' : 'plus'} title={inv.notes ? <DueRighe>{inv.notes}</DueRighe> : 'Aggiungi una nota interna'}
            sub={inv.notes ? 'solo per noi, il cliente non la vede' : undefined}
            onClick={() => setNota(inv.notes ?? '')} />
        </MGroup>

        {(quoteRef || inv.project || onFic) && (
          <MGroup title="Collegata a" note={DAL_COMPUTER}>
            {quoteRef && <MRow icon="file" title="Il preventivo" sub={quoteRef.number ? `Preventivo ${quoteRef.number}` : undefined} onClick={() => navigate(`/quotes/${quoteRef.id}`)} />}
            {inv.project && <MRow icon="cube" title={inv.project.name} sub={`progetto ${inv.project.code}`} onClick={() => navigate(`/projects/${inv.project!.id}`)} />}
            {onFic && <MRow icon="link" title="Su FattureInCloud" sub={`documento n. ${inv.ficDocumentId}${inv.sdiId ? ` · SdI ${inv.sdiId}` : ''}`}
              onClick={inv.ficUrl ? () => window.open(inv.ficUrl!, '_blank') : undefined} />}
          </MGroup>
        )}
      </MPage>

      {primary && (
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" disabled={primary.pending} onClick={primary.onClick}>
            <Icon name={primary.icon} size={12} /> {primary.label}
          </button>
        </div>
      )}

      {/* Segnarla incassata chiede conferma (7/10/2026): il tasto in basso, sul
          telefono, si tocca anche per sbaglio, e chiude tutte le rate con la data di oggi. */}
      <Foglio
        open={confermaIncasso}
        onClose={() => setConfermaIncasso(false)}
        title="Segnare la fattura incassata?"
        primary={(
          <button className="btn btn-primary m-block" disabled={markPaid.isPending}
            onClick={() => { setConfermaIncasso(false); markPaid.mutate(); }}>
            <Icon name="check" size={12} /> Sì, incassata oggi
          </button>
        )}
      >
        <div className="mk-group-note">
          {tipo} {inv.number ?? 'senza numero'} di {leggibile(inv.client.legalName)}, € {fmtEur(inv.total)}: risulterà incassata oggi, con bonifico.
        </div>
      </Foglio>

      <MCampo
        open={nota !== null}
        onClose={() => setNota(null)}
        titolo="Nota interna"
        nota="La vediamo solo noi: il cliente non la riceve."
        campo={<textarea className="inp" rows={4} autoFocus value={nota ?? ''} onChange={(e) => setNota(e.target.value)} />}
        onSalva={() => { if (nota !== null && !updateNotes.isPending) updateNotes.mutate(nota.trim()); }}
        onTogli={inv.notes ? () => updateNotes.mutate('') : undefined}
        togli="Togli la nota"
      />

      {/* Mandarla per email: un tocco e parte con il testo di FattureInCloud; ogni campo si cambia da solo, se serve. */}
      {invio && (
        <Foglio
          open={campo === null}
          onClose={() => setInvio(null)}
          title={`Manda la fattura ${inv.number ?? ''}`}
          primary={(
            <button className="btn btn-primary m-block" disabled={sendEmailFic.isPending}
              onClick={() => sendEmailFic.mutate({ to: invio.to.trim() || undefined, cc: invio.cc.trim() || undefined, subject: invio.subject.trim() || undefined, body: invio.body.trim() || undefined })}>
              <Icon name="send" size={12} /> {sendEmailFic.isPending ? 'Invio in corso…' : 'Manda la fattura'}
            </button>
          )}
        >
          <div className="mk-list">
            {(['to', 'cc', 'subject', 'body'] as CampoInvio[]).map((k) => (
              <MRow key={k} title={k === 'to' ? 'A chi' : k === 'cc' ? 'In copia' : k === 'subject' ? 'Oggetto' : 'Messaggio'}
                trail={valoreInvio(k)} trailTone="mute" onClick={() => { setBozza(invio[k]); setCampo(k); }} />
            ))}
          </div>
          <div className="mk-group-note">Parte da FattureInCloud, con il PDF allegato.</div>
        </Foglio>
      )}
      <MCampo
        open={!!invio && campo !== null}
        onClose={() => setCampo(null)}
        titolo={campo === 'to' ? 'A chi la mandi' : campo === 'cc' ? 'In copia' : campo === 'subject' ? 'Oggetto' : 'Messaggio'}
        nota={campo === 'to' ? 'Vuoto: FattureInCloud usa la mail del contatto principale del cliente.' : campo === 'body' ? 'Vuoto: FattureInCloud usa il suo testo.' : undefined}
        campo={campo === 'body'
          ? <textarea className="inp" rows={5} autoFocus value={bozza} onChange={(e) => setBozza(e.target.value)} />
          : <input className="inp" autoFocus type={campo === 'to' || campo === 'cc' ? 'email' : 'text'} inputMode={campo === 'to' || campo === 'cc' ? 'email' : undefined} value={bozza} onChange={(e) => setBozza(e.target.value)} />}
        onSalva={() => { if (invio && campo) setInvio({ ...invio, [campo]: bozza }); setCampo(null); }}
      />
    </>
  );
}
